/**
 * The one period model every analytics, report and dated-list filter uses.
 *
 * A period is a plain object:
 *   { mode: "all" }
 *   { mode: "month", month: 1-12, year: YYYY }
 *   { mode: "year", year: YYYY }
 *   { mode: "range", startDate: "YYYY-MM-DD", endDate: "YYYY-MM-DD" }  (inclusive)
 *
 * A range may also carry `preset: "today"` — the live dashboard's Today pill.
 * It is display-only: the backend just sees a one-day range.
 *
 * The backend reads the same shape from `period`, `month`, `year`, `startDate`
 * and `endDate`, so a page never translates it by hand — it calls
 * periodToParams() and passes the result through.
 *
 * Every date is a calendar day in the hotel's timezone, because that is how
 * the backend reads YYYY-MM-DD (00:00:00.000 to 23:59:59.999 hotel time).
 * "Today" therefore comes from hotelNow(), not the browser clock, and nothing
 * here uses toISOString(), which would shift the day to UTC.
 */

export const HOTEL_TIME_ZONE = "Africa/Lubumbashi";
// Lubumbashi keeps UTC+2 all year (no daylight saving), so a fixed offset is exact.
export const HOTEL_UTC_OFFSET = "+02:00";

/** Calendar parts of `date` as seen on the hotel's wall clock. */
const hotelParts = (date) => {
  const parts = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: HOTEL_TIME_ZONE, year: "numeric", month: "numeric", day: "numeric",
  })
    .formatToParts(date)
    .forEach(({ type, value }) => { parts[type] = Number(value); });
  return parts;
};

/**
 * Midnight of the hotel's current day, as a local Date — so getFullYear(),
 * getMonth() and getDate() read the hotel's date wherever the browser is.
 */
export const hotelNow = (date = new Date()) => {
  const { year, month, day } = hotelParts(date);
  return new Date(year, month - 1, day);
};

export const PERIOD_MODES = [
  { value: "all", label: "All time" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
  { value: "range", label: "Custom range" },
];

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");

/** The hotel calendar day (YYYY-MM-DD) that an instant falls on. */
export const hotelYmd = (date) => {
  const { year, month, day } = hotelParts(new Date(date));
  return `${year}-${pad(month)}-${pad(day)}`;
};

/** A Date's own calendar parts as YYYY-MM-DD (use on hotelNow()-style dates). */
export const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** True for a real calendar date written as YYYY-MM-DD (rejects 2026-02-30). */
export const isYmd = (s) => {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

// The backend ignores years outside 2000–2100, so they never leave the browser.
const isYear = (y) => Number.isInteger(y) && y >= 2000 && y <= 2100;
const isMonth = (m) => Number.isInteger(m) && m >= 1 && m <= 12;

// Presets, for each page's default.
export const allTime = () => ({ mode: "all" });
export const thisMonth = (now = hotelNow()) => ({ mode: "month", month: now.getMonth() + 1, year: now.getFullYear() });
export const thisYear = (now = hotelNow()) => ({ mode: "year", year: now.getFullYear() });
export const customRange = (startDate, endDate) => ({ mode: "range", startDate, endDate });
export const today = (now = hotelNow()) => ({ ...customRange(toYmd(now), toYmd(now)), preset: "today" });

/** The last `days` days, today included. */
export const lastDays = (days, now = hotelNow()) => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return customRange(toYmd(start), toYmd(now));
};

/** From the same day last month up to today — the old RevenueReports default. */
export const lastMonthToToday = (now = hotelNow()) => {
  const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  return customRange(toYmd(start), toYmd(now));
};

/** Why a period can't be used, or null when it can. */
export const periodError = (p) => {
  if (!p || !p.mode) return "Pick a period";
  switch (p.mode) {
    case "all":
      return null;
    case "month":
      return isMonth(p.month) && isYear(p.year) ? null : "Pick a month and a year";
    case "year":
      return isYear(p.year) ? null : "Pick a year";
    case "range":
      if (!isYmd(p.startDate) || !isYmd(p.endDate)) return "Pick a start and an end date";
      return p.startDate <= p.endDate ? null : "The end date is before the start date";
    default:
      return "Unknown period";
  }
};

/**
 * First and last day the period covers, both inclusive, as YYYY-MM-DD.
 * Null for "All time" (no bounds) and for a period that isn't valid.
 */
export const periodBounds = (p) => {
  if (periodError(p) || p.mode === "all") return null;
  if (p.mode === "range") return { startDate: p.startDate, endDate: p.endDate };
  if (p.mode === "year") return { startDate: `${p.year}-01-01`, endDate: `${p.year}-12-31` };
  const lastDay = new Date(p.year, p.month, 0).getDate();
  return {
    startDate: `${p.year}-${pad(p.month)}-01`,
    endDate: `${p.year}-${pad(p.month)}-${pad(lastDay)}`,
  };
};

/**
 * Query params for the backend's shared period parser. The inclusive
 * startDate/endDate ride along for month and year too, so an endpoint that
 * still only reads the old date params gets the same window.
 * An invalid period sends nothing — the endpoint falls back to its default.
 */
export const periodToParams = (p) => {
  if (periodError(p)) return {};
  const params = { period: p.mode };
  if (p.mode === "month") params.month = p.month;
  if (p.mode === "month" || p.mode === "year") params.year = p.year;
  return { ...params, ...(periodBounds(p) || {}) };
};

/** periodToParams() as a query string, without the leading "?". */
export const periodToQuery = (p) =>
  Object.entries(periodToParams(p))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

const shortDate = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return `${d} ${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;
};

/** Human label: "All time", "October 2026", "2026", "1 Oct 2026 – 31 Oct 2026". */
export const periodLabel = (p) => {
  if (periodError(p)) return "";
  if (p.mode === "all") return "All time";
  if (p.preset === "today") return "Today";
  if (p.mode === "month") return `${MONTH_NAMES[p.month - 1]} ${p.year}`;
  if (p.mode === "year") return String(p.year);
  return p.startDate === p.endDate ? shortDate(p.startDate) : `${shortDate(p.startDate)} – ${shortDate(p.endDate)}`;
};

/**
 * Client-side test for lists that filter rows already in memory.
 * `date` is anything new Date() accepts; compared by its hotel calendar day.
 */
export const isInPeriod = (date, p) => {
  if (periodError(p)) return true;
  if (p.mode === "all") return true;
  const d = new Date(date);
  if (isNaN(d.getTime())) return false;
  const { startDate, endDate } = periodBounds(p);
  const day = hotelYmd(d);
  return day >= startDate && day <= endDate;
};
